import { useCallback, useEffect, useRef, useState } from 'react';

import type { RestoreConfirmationInput, RestoreData } from '@sb/feathers-types';
import { makeNamespacedLog } from '@sb/log';
import { getRestoreData, submitRestoreDecision } from '@sbrc/services';

const log = makeNamespacedLog('useRestoreState');

/** A restore is ordered from the cloud and delivered on updater-bot's
 *  phone-home cycle, so nothing here is urgent — but the transitions the
 *  operator watches (confirmed -> running -> finished) are driven by another
 *  process, so the poll has to be frequent enough to feel live. */
const POLL_INTERVAL_MS = 3_000;

/**
 * The states in which an unreachable box means "still going", not "gone".
 *
 * The apply stops feathers-bot — it is one of the daemons the restore
 * restarts — so every poll fails for the minutes the erase actually takes.
 * Those are the minutes the do-not-power-off warning exists for.
 */
const IN_FLIGHT: ReadonlyArray<RestoreData['state']> = ['confirmed', 'running'];

/**
 * What a failed poll leaves on screen.
 *
 * An unreachable box means "still going" while a restore is in flight and
 * "nothing to show" otherwise, so only a successful poll may decide a
 * restore is over. Blanking here unconditionally is what took the
 * do-not-power-off warning off the screen for the whole apply.
 */
export const retainOnPollFailure = (
  current: RestoreData | null,
): RestoreData | null =>
  current && IN_FLIGHT.includes(current.state) ? current : null;

export type UseRestoreState = {
  data: RestoreData | null;
  /** Set when the last decision the operator submitted was rejected. */
  submitError: string | null;
  isSubmitting: boolean;
  submit: (input: RestoreConfirmationInput) => Promise<void>;
};

/**
 * Poll the box's restore state, and submit the operator's answer.
 *
 * Failures are swallowed rather than thrown: this hook is mounted app-wide,
 * and a box whose feathers-bot is mid-restart must not take the rest of the
 * UI with it. They clear the state too — except while a restore is in
 * flight, where being unable to reach the box is the expected condition and
 * not a reason to take the warning off the screen. Only a successful poll
 * can decide a restore is over.
 */
export function useRestoreState(enabled: boolean = true): UseRestoreState {
  const [data, setData] = useState<RestoreData | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Bumped on every submit so an in-flight poll launched before it cannot
  // land afterwards and paint the pre-decision state back over the screen.
  const generationRef = useRef(0);

  const refresh = useCallback(async () => {
    const generation = generationRef.current;

    try {
      const next = await getRestoreData();

      if (generation !== generationRef.current) return;

      setData(next);
    } catch (error) {
      if (generation !== generationRef.current) return;

      log.debug('poll.fail', 'Could not read restore state', error);

      setData(retainOnPollFailure);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setData(null);

      return undefined;
    }

    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;

      await refresh();
    };

    tick().catch(() => {});

    const interval = setInterval(tick, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [enabled, refresh]);

  const submit = useCallback(
    async (input: RestoreConfirmationInput) => {
      setIsSubmitting(true);
      setSubmitError(null);

      try {
        await submitRestoreDecision(input);

        generationRef.current += 1;

        await refresh();
      } catch (error) {
        log.warn('submit.fail', 'Restore decision was rejected', error);

        setSubmitError(
          (error as Error)?.message ?? 'Could not send your answer',
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [refresh],
  );

  return { data, submitError, isSubmitting, submit };
}
